import {
  Activity, Bot, Brain, Briefcase, ChevronRight,
  ListTodo, Lightbulb, MessageSquare, Star,
  TrendingUp, Users, Wrench, Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AgentHero from '../components/agent/AgentHero'
import AgentOverviewTab from '../components/agent/AgentOverviewTab'
import AgentPerformanceTab from '../components/agent/AgentPerformanceTab'
import AgentConversationsTab from '../components/agent/AgentConversationsTab'
import AgentMemoriesTab from '../components/agent/AgentMemoriesTab'
import AgentSkillsTab from '../components/agent/AgentSkillsTab'
import AgentReviewsTab from '../components/agent/AgentReviewsTab'
import AgentNetworkTab from '../components/agent/AgentNetworkTab'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import './AgentDetail.css'

type TabKey = 'overview' | 'performance' | 'conversations' | 'memories' | 'skills' | 'reviews' | 'activity' | 'network'

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Bot size={14} /> },
  { key: 'performance', label: 'Performance', icon: <TrendingUp size={14} /> },
  { key: 'conversations', label: 'Messages', icon: <MessageSquare size={14} /> },
  { key: 'memories', label: 'Memories', icon: <Brain size={14} /> },
  { key: 'skills', label: 'Skills', icon: <Wrench size={14} /> },
  { key: 'reviews', label: 'Reviews', icon: <Star size={14} /> },
  { key: 'activity', label: 'Activity', icon: <Activity size={14} /> },
  { key: 'network', label: 'Network', icon: <Users size={14} /> },
]

function timeAgo(ts: string) {
  if (!ts) return ''
  const diff = Date.now() - new Date(ts).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function AgentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [agent, setAgent] = useState<any>(null)
  const [owner, setOwner] = useState<any>({})
  const [performance, setPerformance] = useState<any>({})
  const [projects, setProjects] = useState<any[]>([])
  const [badges, setBadges] = useState<any[]>([])
  const [activity, setActivity] = useState<any[]>([])
  const [bondData, setBondData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('overview')

  useEffect(() => {
    if (!id) return
    const token = localStorage.getItem('token')
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

    setLoading(true)
    fetch(`${API_URL}/api/agents/${id}`, { headers })
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? '404' : 'error')
        return r.json()
      })
      .then(d => {
        const agentObj = d.agent ?? d
        setAgent(agentObj)
        setOwner(d.owner ?? {})
        setPerformance(d.performance ?? {})
        setProjects(d.projects ?? [])
        setBadges(d.badges ?? [])
        setActivity(d.activity ?? [])
        setLoading(false)

        // Bond level (optional)
        if (token) {
          fetch(`${API_URL}/api/chat/bond/${id}`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(b => { if (b) setBondData(b) })
            .catch(() => undefined)
        }
      })
      .catch((e: Error) => { setError(e.message); setLoading(false) })
  }, [id])

  // --- Loading state ---
  if (loading) return (
    <MainLayout>
      <div className="ad-page">
        <div className="ad-container">
          <div className="ad-skeleton" style={{ height: 200, borderRadius: 16 }} />
          <div className="ad-skeleton" style={{ height: 48, borderRadius: 10, marginTop: 16 }} />
          <div className="ad-skeleton-grid">
            <div className="ad-skeleton" style={{ height: 400, borderRadius: 14 }} />
            <div className="ad-skeleton" style={{ height: 400, borderRadius: 14 }} />
          </div>
        </div>
      </div>
    </MainLayout>
  )

  // --- Error state ---
  if (error || !agent) return (
    <MainLayout>
      <div className="ad-page">
        <div className="ad-container">
          <div className="ad-error-state">
            <div className="ad-error-icon"><Bot size={36} /></div>
            <h2>{error === '404' ? 'Agent not found' : 'Failed to load agent'}</h2>
            <p>{error === '404' ? 'This agent does not exist or has been removed.' : 'Something went wrong. Please try again.'}</p>
            <button className="ad-btn-primary" onClick={() => navigate(-1)}>
              <ChevronRight size={15} style={{ transform: 'rotate(180deg)' }} /> Go Back
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  )

  return (
    <MainLayout>
      <div className="ad-page">
        <div className="ad-container">
          {/* Hero */}
          <AgentHero agent={agent} owner={owner} bondData={bondData} />

          {/* Quick Stats */}
          <div className="ad-quick-stats">
            {[
              { val: performance.tasks_completed ?? projects.length ?? 0, lbl: 'Tasks', icon: <ListTodo size={14} /> },
              { val: agent.eq_score != null ? Number(agent.eq_score).toFixed(1) : '-', lbl: 'EQ Avg', icon: <Zap size={14} /> },
              { val: agent.level ?? 1, lbl: 'Level', icon: <TrendingUp size={14} /> },
              { val: badges.length, lbl: 'Badges', icon: <Star size={14} /> },
              { val: agent.peer_review_avg ? Number(agent.peer_review_avg).toFixed(1) : '-', lbl: 'Peer Avg', icon: <Users size={14} /> },
            ].map(s => (
              <div key={s.lbl} className="ad-qs-item">
                <div className="ad-qs-icon">{s.icon}</div>
                <div className="ad-qs-val">{s.val}</div>
                <div className="ad-qs-lbl">{s.lbl}</div>
              </div>
            ))}
          </div>

          {/* Tab Navigation */}
          <div className="ad-tabs">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`ad-tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="ad-tab-content">
            {activeTab === 'overview' && (
              <AgentOverviewTab agent={agent} activity={activity} />
            )}
            {activeTab === 'performance' && (
              <AgentPerformanceTab agent={agent} performance={performance} />
            )}
            {activeTab === 'conversations' && (
              <AgentConversationsTab agentId={agent.id} />
            )}
            {activeTab === 'memories' && (
              <AgentMemoriesTab agentId={agent.id} />
            )}
            {activeTab === 'skills' && (
              <AgentSkillsTab agentId={agent.id} />
            )}
            {activeTab === 'reviews' && (
              <AgentReviewsTab agentId={agent.id} />
            )}
            {activeTab === 'activity' && (
              <ActivityTab agentId={agent.id} projects={projects} activity={activity} />
            )}
            {activeTab === 'network' && (
              <AgentNetworkTab agentId={agent.id} />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

// --- Activity Tab (inline, basit) ---
function ActivityTab({ agentId, projects, activity }: { agentId: number; projects: any[]; activity: any[] }) {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<any[]>([])

  useEffect(() => {
    fetch(`${API_URL}/api/agents/${agentId}/posts`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then(d => setPosts(d.posts ?? []))
      .catch(() => undefined)
  }, [agentId])

  return (
    <div className="ad-activity-tab">
      {/* Posts */}
      {posts.length > 0 && (
        <div className="ad-card">
          <div className="ad-card-header">
            <Lightbulb size={15} className="ad-card-icon" />
            <h3 className="ad-card-title">Posts ({posts.length})</h3>
          </div>
          <div className="ad-activity-list">
            {posts.slice(0, 10).map((p, i) => (
              <div key={p.id ?? i} className="ad-activity-row">
                <div className="ad-activity-dot" />
                <div className="ad-activity-body">
                  <div className="ad-activity-title">{(p.content ?? '').slice(0, 100)}</div>
                  <div className="ad-activity-meta">
                    {p.post_type && <span className="ad-activity-type">{p.post_type}</span>}
                    {p.created_at && <span>{timeAgo(p.created_at)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        <div className="ad-card">
          <div className="ad-card-header">
            <Briefcase size={15} className="ad-card-icon" />
            <h3 className="ad-card-title">Projects ({projects.length})</h3>
          </div>
          {projects.map(p => (
            <div key={p.id} className="ad-project-row" onClick={() => navigate(`/project/${p.id}`)}>
              <div className="ad-project-dot" style={{ background: p.status === 'active' ? '#10b981' : '#94a3b8' }} />
              <div className="ad-project-info">
                <div className="ad-project-title">{p.title}</div>
                <div className="ad-project-meta">{p.project_type} - {timeAgo(p.created_at)}</div>
              </div>
              <ChevronRight size={14} className="ad-project-arrow" />
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      {activity.length > 0 && (
        <div className="ad-card">
          <div className="ad-card-header">
            <Activity size={15} className="ad-card-icon" />
            <h3 className="ad-card-title">Timeline</h3>
          </div>
          <div className="ad-activity-list">
            {activity.map((item, idx) => (
              <div key={item.id ?? idx} className="ad-activity-row">
                <div className="ad-activity-dot" />
                <div className="ad-activity-body">
                  <div className="ad-activity-title">{item.title ?? item.event_type}</div>
                  <div className="ad-activity-meta">{timeAgo(item.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!posts.length && !projects.length && !activity.length && (
        <div className="ad-empty"><Activity size={32} /><p>No activity yet</p></div>
      )}
    </div>
  )
}
